import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { DeliveryChannel } from '../giftCoupon/giftCoupon.interface';
import {
  ChannelBalanceSummary,
  ChannelLedgerListResponse,
  ChannelLedgerManualDto,
  ChannelReconcileResponse,
  RepairUnrecordedResponse,
  UnrecordedResponse,
} from './giftChannelLedger.interface';

// 발송매체 충전금 미러 원장 (Admin, GiftAgent / 삭제만 Admin)
// 구서버엔 없는 엔드포인트 — 전부 404 가능(호출부 가드), 스펙: docs/gift-channel-ledger-spec.md
const BASE = `/${LowerRole.Admin}/gift-channel-ledger`;

// 4채널 잔액 요약 — 기록 없는 채널도 0으로 온다
export const getChannelBalances = async (): Promise<ChannelBalanceSummary[]> => {
  const { data } = await api_.get<ChannelBalanceSummary[]>(`${BASE}/balances`);
  return data;
};

// 채널 원장 목록 (occurredAt 오름차순 + 누적잔액 balanceAfter)
export const getChannelLedger = async (
  channel: DeliveryChannel,
  from?: string,
  to?: string,
): Promise<ChannelLedgerListResponse> => {
  const params = new URLSearchParams({ channel });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const { data } = await api_.get<ChannelLedgerListResponse>(`${BASE}?${params}`);
  return data;
};

// 대사 — 실제 사이트 잔액 입력 → diff = actual − ours
export const reconcileChannel = async (
  channel: DeliveryChannel,
  actualBalance: number,
): Promise<ChannelReconcileResponse> => {
  const params = new URLSearchParams({ channel, actualBalance: String(actualBalance) });
  const { data } = await api_.get<ChannelReconcileResponse>(
    `${BASE}/reconcile?${params}`,
  );
  return data;
};

export const createChannelDeposit = async (dto: ChannelLedgerManualDto) => {
  const { data } = await api_.post(`${BASE}/deposit`, dto);
  return data;
};

export const createChannelExternalUse = async (dto: ChannelLedgerManualDto) => {
  const { data } = await api_.post(`${BASE}/external-use`, dto);
  return data;
};

export const createChannelRestore = async (
  dto: ChannelLedgerManualDto & { couponIds?: string[] },
) => {
  const { data } = await api_.post(`${BASE}/restore`, dto);
  return data;
};

// 보정 — 부호 자유, memo 필수. 도입 시 채널별 기초잔액 세팅도 이걸로.
export const createChannelAdjust = async (
  dto: ChannelLedgerManualDto & { memo: string },
) => {
  const { data } = await api_.post(`${BASE}/adjust`, dto);
  return data;
};

// 수동 항목 삭제 (Admin 한정, Deduct는 서버가 400 거부)
export const deleteChannelLedgerEntry = async (
  id: string,
): Promise<{ deleted: boolean }> => {
  const { data } = await api_.delete<{ deleted: boolean }>(`${BASE}/${id}`);
  return data;
};

// 미기장 발송 검출 — 도입 이전 레거시 발송분 제외를 위해 from은 도입일 이후 권장
export const getUnrecordedDeliveries = async (
  from?: string,
  to?: string,
): Promise<UnrecordedResponse> => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  const { data } = await api_.get<UnrecordedResponse>(
    `${BASE}/unrecorded${query ? `?${query}` : ''}`,
  );
  return data;
};

// 미기장 소급 기장 (recordable 그룹만, 멱등)
export const repairUnrecordedDeliveries = async (
  from?: string,
  to?: string,
): Promise<RepairUnrecordedResponse> => {
  const { data } = await api_.post<RepairUnrecordedResponse>(
    `${BASE}/repair-unrecorded`,
    { ...(from && { from }), ...(to && { to }) },
  );
  return data;
};
